import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Avatar,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    Drawer,
    Autocomplete,
    TextField
} from '@mui/material';
import {
    Person as PersonIcon,
    Group as GroupIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import useFetch from '../../../../hooks/useFetch';
import RTL from '../../../../utils/RTL';
import TeacherScheduleTimeGrid from './TeacherScheduleTimeGrid';
import TeacherScheduleForm from './TeacherScheduleForm';

export default function TeacherScheduleOverview() {
    // Optional single-teacher filter; empty = all teachers
    const [selectedTeacher, setSelectedTeacher] = useState(null);

    // Fetch teachers (page_size high enough to bypass the default 15-item page)
    const { data: teachersData } = useFetch('/accounts/api/v1/profile/teachers/?page_size=200');
    const teacherOptions = useMemo(() =>
        (teachersData?.results || []).map((teacher) => ({
            id: teacher.user.id,
            label: `${teacher.user.first_name} ${teacher.user.last_name}`
        })), [teachersData]);

    // Fetch institute settings for default work hours
    const { data: instituteSettings } = useFetch('/institutes/api/v1/teacher-working-days/institute-settings/');

    // The time-grid picks its day client-side, so it always needs the full week
    const apiUrl = useMemo(() => {
        const params = new URLSearchParams();
        if (selectedTeacher) {
            params.append('teacher_ids', String(selectedTeacher.id));
        }
        return `/operations/api/v1/student-registrations/teacher-schedule-overview/?${params.toString()}`;
    }, [selectedTeacher]);

    // Fetch schedule data
    const { data: scheduleData, loading: scheduleLoading, refetch } = useFetch(apiUrl);

    // Dialog showing the students of a group class
    const [groupDialog, setGroupDialog] = useState(null);

    // Drawer editing one teacher's working days, opened from the grid header
    const [editTeacher, setEditTeacher] = useState(null);

    return (
        <RTL>
            <Box sx={{ p: 3 }}>

                {/* Optional single-teacher filter. Selecting a teacher also keeps
                    their column visible on off days (see TeacherScheduleTimeGrid),
                    which is how a teacher without any schedule yet stays reachable
                    for the edit drawer. */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Autocomplete
                        options={teacherOptions}
                        value={selectedTeacher}
                        onChange={(event, value) => setSelectedTeacher(value)}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        noOptionsText="آموزگاری یافت نشد"
                        sx={{ width: { xs: '100%', sm: 320 } }}
                        renderInput={(params) => (
                            <TextField {...params} size="small" label="آموزگار (همه)" />
                        )}
                    />
                </Box>

                {/* Loading State */}
                {scheduleLoading && (
                    <Box>
                        <Skeleton variant="rounded" height={40} width={420} sx={{ mx: 'auto', mb: 2, maxWidth: '100%' }} />
                        <Skeleton variant="rounded" height={480} />
                    </Box>
                )}

                {/* Weekly time grid */}
                {scheduleData && !scheduleLoading && scheduleData.teachers?.length > 0 && (
                    <TeacherScheduleTimeGrid
                        teachers={scheduleData.teachers}
                        instituteSettings={instituteSettings}
                        onGroupClick={setGroupDialog}
                        onEditTeacher={setEditTeacher}
                    />
                )}

                {/* Empty State */}
                {scheduleData && !scheduleLoading && (!scheduleData.teachers || scheduleData.teachers.length === 0) && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6" color="text.secondary">
                            هیچ آموزگاری یافت نشد
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            لطفاً فیلترهای خود را بررسی کنید
                        </Typography>
                    </Box>
                )}

                {/* Working-days edit drawer: the grid stays visible behind it */}
                {/* MUI mirrors Drawer anchors in RTL: "right" renders on the visual left */}
                <Drawer
                    anchor="right"
                    open={Boolean(editTeacher)}
                    onClose={() => setEditTeacher(null)}
                    PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, pt: '4rem' } }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 0 }}>
                        <Typography variant="h6">
                            {`ویرایش برنامه کاری ${editTeacher?.full_name || ''}`}
                        </Typography>
                        <IconButton size="small" onClick={() => setEditTeacher(null)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    {editTeacher && (
                        <TeacherScheduleForm
                            key={editTeacher.id}
                            teacherId={editTeacher.id}
                            onSaved={refetch}
                        />
                    )}
                </Drawer>

                {/* Group class students dialog */}
                <Dialog
                    open={Boolean(groupDialog)}
                    onClose={() => setGroupDialog(null)}
                    fullWidth
                    maxWidth="xs"
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GroupIcon color="secondary" fontSize="small" />
                            {groupDialog?.course?.course_name}
                        </Box>
                        <IconButton size="small" onClick={() => setGroupDialog(null)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        {groupDialog?.students?.length ? (
                            <List dense>
                                {groupDialog.students.map((student, index) => (
                                    <ListItem key={student.id ?? index} disableGutters>
                                        <ListItemAvatar>
                                            <Avatar sx={{ width: 32, height: 32 }}>
                                                <PersonIcon fontSize="small" />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText primary={student.full_name} />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                هنرجویی ثبت‌نام نکرده است
                            </Typography>
                        )}
                    </DialogContent>
                </Dialog>

            </Box>
        </RTL>
    );
}
